/**
 * Native HTML5 drag-and-drop reordering for Formik FieldArrays (VER-109).
 *
 * No new dependency: the admin app ships no drag-and-drop library, and the file-
 * ownership rules forbid adding one. This hook wires the browser's native DnD to
 * a Formik `move(from, to)` callback. Every reorderable list ALSO exposes
 * move-up / move-down buttons (see ReorderControls) so reordering is fully
 * keyboard-accessible and testable without synthesising drag events.
 */
import { useRef } from "react";
import type { DragEvent } from "react";

export type DragReorder = {
    /** Spread onto the drag handle element (the only draggable part). */
    handleProps: (index: number) => {
        draggable: true;
        onDragStart: (e: DragEvent) => void;
        onDragEnd: () => void;
    };
    /** Spread onto the row/card that can receive a drop. */
    dropZoneProps: (index: number) => {
        onDragOver: (e: DragEvent) => void;
        onDrop: (e: DragEvent) => void;
    };
};

export const useDragReorder = (move: (from: number, to: number) => void): DragReorder => {
    const dragIndex = useRef<number | null>(null);

    return {
        handleProps: (index) => ({
            draggable: true,
            onDragStart: (e) => {
                dragIndex.current = index;
                e.dataTransfer.effectAllowed = "move";
            },
            onDragEnd: () => {
                dragIndex.current = null;
            },
        }),
        dropZoneProps: (index) => ({
            onDragOver: (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
            },
            onDrop: (e) => {
                e.preventDefault();
                const from = dragIndex.current;
                if (from !== null && from !== index) {
                    move(from, index);
                }
                dragIndex.current = null;
            },
        }),
    };
};

/** Renumber a list's `order` fields to match array position (1-based). */
export const renumber = <T extends { order: number }>(items: T[]): T[] =>
    items.map((item, i) => ({ ...item, order: i + 1 }));

/** Immutably move an item from one index to another (no-op if out of range). */
export const arrayMove = <T>(items: T[], from: number, to: number): T[] => {
    if (to < 0 || to >= items.length || from < 0 || from >= items.length || from === to) {
        return items;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};

/** Immutably insert an item at an index. */
export const arrayInsert = <T>(items: T[], index: number, item: T): T[] => {
    const next = [...items];
    next.splice(index, 0, item);
    return next;
};

/** Immutably remove the item at an index. */
export const arrayRemove = <T>(items: T[], index: number): T[] => items.filter((_, i) => i !== index);
